import React, { useMemo } from 'react';
import { StudentLoansInline } from '@highereducation/hen-ui';
import { Render } from '@puckeditor/core';
import type { Config, Data } from '@puckeditor/core';
import type { StudentLoansInlineDslContent } from '@de/types/types';
import { DataSourceManagerProvider } from '@de/providers/DataSourceManager';
import {
  applyBindings,
  type ComponentBindingMeta,
} from '@de/utils/binding-enhancer';
import { COMPONENT_TRACKING_DEFAULTS } from '@de/components/Trackable';

const EMPTY_DATA_SOURCES = {};
const EMPTY_FLOW_CONTEXT = {};

const COMPONENT_BINDING_META: Readonly<Record<string, ComponentBindingMeta>> = {
  StudentLoansInline: {
    eventPropNames: ['onCtaClick'],
    clickPropName: 'onCtaClick',
    outboundUrlPropPath: 'cta.href',
  },
};

// Puck passes render props as an untyped record after bindings are applied.
// Runtime validation below keeps the HEN UI component from receiving bad props.
const StudentLoansInlineComponent =
  StudentLoansInline as unknown as React.ComponentType<Record<string, unknown>>;

const STUDENT_LOANS_INLINE_CONFIG = applyBindings(
  {
    components: {
      StudentLoansInline: {
        fields: {},
        render: (props: Record<string, unknown>) => (
          <StudentLoansInlineComponent {...props} />
        ),
      },
    },
    root: {
      fields: {},
      render: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    },
  } as Config,
  COMPONENT_BINDING_META,
  COMPONENT_TRACKING_DEFAULTS,
);

interface StudentLoansInlinePlacementProps {
  readonly content?: StudentLoansInlineDslContent;
  readonly configId?: string;
}

interface PlacementErrorBoundaryProps {
  readonly children: React.ReactNode;
  readonly configId?: string;
}

interface PlacementErrorBoundaryState {
  readonly hasError: boolean;
}

class PlacementErrorBoundary extends React.Component<
  PlacementErrorBoundaryProps,
  PlacementErrorBoundaryState
> {
  state: PlacementErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PlacementErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(
      `[StudentLoansInlinePlacement] Render error for config "${this.props.configId ?? 'unknown'}":`,
      error,
      info.componentStack,
    );
  }

  render(): React.ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

export const StudentLoansInlinePlacement: React.FC<
  StudentLoansInlinePlacementProps
> = ({ content, configId }) => {
  const data = useMemo(() => buildStudentLoansInlineData(content), [content]);

  if (!data) {
    return null;
  }

  return (
    <PlacementErrorBoundary configId={configId}>
      <DataSourceManagerProvider
        dataSources={EMPTY_DATA_SOURCES}
        flowContext={EMPTY_FLOW_CONTEXT}
      >
        <Render config={STUDENT_LOANS_INLINE_CONFIG} data={data} />
      </DataSourceManagerProvider>
    </PlacementErrorBoundary>
  );
};

function buildStudentLoansInlineData(
  content?: StudentLoansInlineDslContent,
): Data | null {
  if (!isValidStudentLoansInlineContent(content)) {
    return null;
  }

  return {
    content: [
      {
        type: 'StudentLoansInline',
        props: {
          ...content.props,
          '_meta.tracking': { location: 'voyager-exit-credible' },
        },
      },
    ],
    root: { props: {} },
  } as Data;
}

function isValidStudentLoansInlineContent(
  content?: StudentLoansInlineDslContent,
): content is StudentLoansInlineDslContent {
  if (content?.type !== 'StudentLoansInline') {
    return false;
  }

  const props = content.props;
  if (!isPlainObject(props)) {
    return false;
  }

  const partner = props.partner;
  const cta = props.cta;

  return (
    isNonEmptyString(props.title) &&
    isNonEmptyString(props.subtitle) &&
    isNonEmptyString(props.disclosure) &&
    isPlainObject(partner) &&
    isNonEmptyString(partner.label) &&
    isNonEmptyString(partner.logo) &&
    isNonEmptyString(partner.name) &&
    typeof partner.rating === 'number' &&
    isPlainObject(cta) &&
    isNonEmptyString(cta.label) &&
    isNonEmptyString(cta.href)
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
